import type { FlatMultiPinsRecord } from '@/features/map/map-data-v5/map-data-v5-service';
import type { MapDataV5_jsonld_page_read } from '@/features/map/map-data-v5/map-data-v5-type';

import React from 'react';

import useComponentDataByIri from '@/core/features/a-component/hooks/use-component-data-by-iri';
import { updateComponentIriParam } from '@/core/features/a-component/services/component-service';
import useDynamicPageComponentByType from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-component-by-type';
import { useClientUrl } from '@/core/features/app/app-atoms';
import useAppEvents from '@/core/features/app/use-app-events';
import { useRouterLink } from '@/core/features/router/router-link';
import { atom, useAtom, useAtomValue, useSetAtom } from '@/core/features/store/atom-store';
import { getURL, getUrlPathWithSearch } from '@/core/utils/url';
import { useGoogleMapInstance } from '@/features/map/google-map/google-map-state';
import { logMap } from '@/features/map/map-container/map-logger';
import { mapScreenSizeAtom, useMapActivePoiIdState } from '@/features/map/map-container/map-state';
import { useAutoLoadMoreSetting } from '@/features/map/map-data-v5/auto-load-more-button-state';
import {
    type BoundariesType,
    getBoundariesObject,
    getMultiRecordFromPins,
} from '@/features/map/map-data-v5/map-data-v5-service';

export const mapBoundariesAtom = atom<BoundariesType | null>(null);
export const setMapBoundariesAtom = atom(null, (get, set, boundaries: BoundariesType | null) => {
    logMap('setMapBoundariesAtom', {
        boundaries,
    });
    set(mapBoundariesAtom, boundaries);
});

export const mapDataV5DefaultAtom = atom<MapDataV5_jsonld_page_read | null>(null);
export const mapDataV5ByBoundariesAtom = atom<MapDataV5_jsonld_page_read | null>(null);
export const mapDataV5ByPoiAtom = atom<MapDataV5_jsonld_page_read | null>(null);

export const multiPinsAtom = atom<FlatMultiPinsRecord>((get) => {
    const mapDataDefault = get(mapDataV5DefaultAtom);
    const mapDataByBoundaries = get(mapDataV5ByBoundariesAtom);
    const pins = mapDataByBoundaries?.attributes?.pins ?? mapDataDefault?.attributes?.pins;
    if (!pins) {
        return {};
    }
    return getMultiRecordFromPins(pins);
});

export const useMultiPins = () => useAtomValue(multiPinsAtom);

export const isMapDataV5DefaultLoadingAtom = atom(false);
export const isMapDataV5ByBoundariesLoadingAtom = atom(false);
export const isMapDataV5BySpotLoadingAtom = atom(false);

export const mapDataV5EntryTargetAtom = atom<null | string>(null);
export const useSetMapDataV5EntryTarget = () => useSetAtom(mapDataV5EntryTargetAtom);

const isBoundariesLivePricesLoadingAtom = atom(false);

const isMapPoiLivePricesLoadingAtom = atom(false);
export const useMapPoiLivePricesLoading = () => useAtomValue(isMapPoiLivePricesLoadingAtom);

const isLoadMoreButtonLoadingAtom = atom(false);
export const useLoadMoreButtonLoading = () => useAtomValue(isLoadMoreButtonLoadingAtom);

const boundariesTargetAtom = atom<null | string>(null);
const boundariesTargetWithBoundariesAtom = atom<null | string>(null);
export const boundariesLoadMoreLivePricesTargetAtom = atom<null | string>(null);

export const useHydrateMapDataV5Default = () => {
    const setMapDataStateDefault = useSetAtom(mapDataV5DefaultAtom);
    const setMapDataV5DefaultLoading = useSetAtom(isMapDataV5DefaultLoadingAtom);
    const mapDataV5EntryTarget = useAtomValue(mapDataV5EntryTargetAtom);
    const boundaries = useAtomValue(mapBoundariesAtom);

    const component = useDynamicPageComponentByType<MapDataV5_jsonld_page_read>('MapDataV5');

    const mapScreenSize = useAtomValue(mapScreenSizeAtom);

    const addedUserQuery = {
        viewportHeight: `${mapScreenSize?.height ?? window.innerHeight}`,
        viewportWidth: `${mapScreenSize?.width ?? window.innerWidth}`,
    };

    const { data: mapDataInitial, isLoading: isInitialLoading } = useComponentDataByIri<MapDataV5_jsonld_page_read>(
        component?.['@id'] ?? null,
        { addedUserQuery, queryName: 'mapDataInitial', ...boundaries },
    );

    const { data: mapDataByEntryTarget, isLoading: isEntryTargetLoading } =
        useComponentDataByIri<MapDataV5_jsonld_page_read>(mapDataV5EntryTarget, {
            addedUserQuery: { ...addedUserQuery },
            queryName: 'mapDataByEntryTarget',
        });

    // decide what data is currently used
    const mapDataDefault = mapDataByEntryTarget ?? mapDataInitial;
    const isDefaultLoading = isInitialLoading || isEntryTargetLoading;

    // hydrate default state atom
    React.useEffect(() => {
        if (!mapDataDefault) {
            // warning: this is not ideal but since the vertical toggle on the map changes the client url
            // it will lead to component?.['@id'] being null, and therefore mapDataInitial being null as well
            // this will lead to the map showing nothing when switching verticals
            // therefore only update the state if we have data
            return;
        }

        setMapDataStateDefault(mapDataDefault);
    }, [component, mapDataDefault, setMapDataStateDefault]);

    // update loading state
    React.useEffect(() => {
        setMapDataV5DefaultLoading(isDefaultLoading);
    }, [isDefaultLoading, setMapDataV5DefaultLoading]);
};

export const useHydrateMapDataV5ByBoundaries = () => {
    // if available use initial Boundary from DynamicPage for first request
    const dynamicPageMapData = useDynamicPageComponentByType<MapDataV5_jsonld_page_read>('MapDataV5');
    const initialBoundary = dynamicPageMapData?.meta.initialBoundary
        ? { ...dynamicPageMapData?.meta.initialBoundary, zoom: dynamicPageMapData?.meta.defaultZoom }
        : null;

    // update boundaries default data target
    const mapBoundaries = useAtomValue(mapBoundariesAtom) ?? initialBoundary;
    const mapDataV5Default = useAtomValue(mapDataV5DefaultAtom);
    const setBoundariesTarget = useSetAtom(boundariesTargetAtom);

    // get boundaries load more live prices target
    const boundariesLoadMoreLivePricesTarget = useAtomValue(boundariesLoadMoreLivePricesTargetAtom);
    const { data: boundariesLoadMoreLivePricesData, isLoading: isBoundariesLoadMoreLivePricesLoading } =
        useComponentDataByIri<MapDataV5_jsonld_page_read>(boundariesLoadMoreLivePricesTarget, {
            config: { staleTime: 0 },
            queryName: 'mapBoundariesLoadMoreLivePricesTarget',
        });

    React.useEffect(() => {
        setBoundariesTarget(mapDataV5Default?.meta.boundariesRequestTarget ?? null);
    }, [mapDataV5Default?.meta.boundariesRequestTarget, setBoundariesTarget]);

    // build boundaries default data request target
    const boundariesTarget = useAtomValue(boundariesTargetAtom);
    const [boundariesTargetWithBoundaries, setBoundariesTargetWithBoundaries] = useAtom(
        boundariesTargetWithBoundariesAtom,
    );
    React.useEffect(() => {
        if (isBoundariesLoadMoreLivePricesLoading) {
            // skip updating the boundaries target while load more is running
            // this way we defer the request until the load more live prices are available
            return;
        }

        if (!boundariesTarget || !mapBoundaries) {
            setBoundariesTargetWithBoundaries(null);
            return;
        }

        setBoundariesTargetWithBoundaries(
            updateComponentIriParam('userQuery', boundariesTarget, mapBoundaries).toString(),
        );
    }, [boundariesTarget, isBoundariesLoadMoreLivePricesLoading, mapBoundaries, setBoundariesTargetWithBoundaries]);

    // get boundaries default data
    const { data: boundariesData, isLoading: isBoundariesLoading } = useComponentDataByIri<MapDataV5_jsonld_page_read>(
        boundariesTargetWithBoundaries,
        { config: { staleTime: 0 }, queryName: 'mapBoundariesData' },
    );

    // get boundaries default live prices data
    const { data: boundariesLivePricesData, isLoading: isBoundariesLivePricesLoading } =
        useComponentDataByIri<MapDataV5_jsonld_page_read>(
            boundariesData?.meta.boundariesLivePricesRequestTarget ?? null,
            { config: { staleTime: 0 }, queryName: 'mapBoundariesLivePricesRequestTarget' },
        );

    // decide what boundaries data is currently used
    const mapDataByBoundaries = boundariesLoadMoreLivePricesData ?? boundariesLivePricesData ?? boundariesData;

    // reset boundaries load more live prices target once the user moves the map
    // but only once the boundaries data is loaded, otherwise we would show stale data for a moment
    const setBoundariesLoadMoreLivePricesTarget = useSetAtom(boundariesLoadMoreLivePricesTargetAtom);
    React.useEffect(() => {
        if (isBoundariesLoading || isBoundariesLivePricesLoading) {
            return;
        }
        setBoundariesLoadMoreLivePricesTarget(null);
    }, [
        isBoundariesLivePricesLoading,
        isBoundariesLoading,
        setBoundariesLoadMoreLivePricesTarget,
        boundariesTargetWithBoundaries, // Additional dependency to reset livePriceTarget when boundary changes
    ]);

    // hydrate boundaries state atom
    const setMapDataByBoundaries = useSetAtom(mapDataV5ByBoundariesAtom);
    React.useEffect(() => {
        setMapDataByBoundaries(mapDataByBoundaries);
    }, [mapDataByBoundaries, setMapDataByBoundaries]);

    // update boundaries loading state
    const setMapDataV5ByBoundariesLoading = useSetAtom(isMapDataV5ByBoundariesLoadingAtom);
    const isAutoLoadMoreFeatureFlagEnabled = useAutoLoadMoreSetting();
    React.useEffect(() => {
        if (isAutoLoadMoreFeatureFlagEnabled) {
            setMapDataV5ByBoundariesLoading(isBoundariesLoading || isBoundariesLivePricesLoading);
        } else {
            setMapDataV5ByBoundariesLoading(
                isBoundariesLoading || isBoundariesLivePricesLoading || isBoundariesLoadMoreLivePricesLoading,
            );
        }
    }, [
        isBoundariesLivePricesLoading,
        isBoundariesLoadMoreLivePricesLoading,
        isBoundariesLoading,
        isAutoLoadMoreFeatureFlagEnabled,
        setMapDataV5ByBoundariesLoading,
    ]);

    const setBoundariesLivePricesLoading = useSetAtom(isBoundariesLivePricesLoadingAtom);
    // update atom state with local query state for boundaries live prices loading
    React.useEffect(() => {
        setBoundariesLivePricesLoading(isBoundariesLivePricesLoading);
    }, [isBoundariesLivePricesLoading, setBoundariesLivePricesLoading]);

    // update load more loading state
    const setLoadMoreButtonLoading = useSetAtom(isLoadMoreButtonLoadingAtom);
    React.useEffect(() => {
        setLoadMoreButtonLoading(isBoundariesLoadMoreLivePricesLoading);
    }, [isBoundariesLoadMoreLivePricesLoading, setLoadMoreButtonLoading]);
};

export const useHydrateMapDataV5BySpot = () => {
    const setMapDataV5BySpotLoading = useSetAtom(isMapDataV5BySpotLoadingAtom);
    const mapDataV5EntryTarget = useAtomValue(mapDataV5EntryTargetAtom);

    const { isLoading } = useComponentDataByIri<MapDataV5_jsonld_page_read>(mapDataV5EntryTarget, {
        queryName: 'mapDataV5EntryTarget',
    });

    // update loading state
    React.useEffect(() => {
        setMapDataV5BySpotLoading(isLoading);
    }, [isLoading, setMapDataV5BySpotLoading]);
};

export const useHydrateMapDataV5ByPoi = () => {
    const setMapDataByPoi = useSetAtom(mapDataV5ByPoiAtom);
    const setPoiLivePricesLoading = useSetAtom(isMapPoiLivePricesLoadingAtom);

    const mapDataByBoundaries = useAtomValue(mapDataV5ByBoundariesAtom);
    const mapDataDefault = useAtomValue(mapDataV5DefaultAtom);
    const isMapDataV5ByBoundariesLoading = useAtomValue(isMapDataV5ByBoundariesLoadingAtom);

    const multiPins = useMultiPins();
    const activePoiId = useMapActivePoiIdState();

    const poiRequestTargetWithIds = React.useMemo((): null | string => {
        const pins = mapDataByBoundaries?.attributes?.pins;
        const poiRequestTarget =
            mapDataByBoundaries?.meta.poiRequestTarget ?? mapDataDefault?.meta.poiRequestTarget ?? null;

        if ((!pins && !activePoiId) || !poiRequestTarget) {
            return null;
        }

        const poiIds = Object.values({ ...pins, ...multiPins }).flatMap((pin) => {
            if (!pin || (pin.markerType !== 'poi' && pin.markerType !== 'multiPolygon')) {
                return [];
            }
            return pin.poiId;
        });

        if (activePoiId && !poiIds.includes(activePoiId)) {
            poiIds.push(activePoiId);
        }

        const updatePoiRequestTarget = updateComponentIriParam('additionalParameters', poiRequestTarget, {
            poiId: poiIds.join(','),
        });

        return updatePoiRequestTarget.pathname + updatePoiRequestTarget.search;
    }, [
        activePoiId,
        mapDataByBoundaries?.attributes?.pins,
        mapDataByBoundaries?.meta.poiRequestTarget,
        mapDataDefault?.meta.poiRequestTarget,
        multiPins,
    ]);

    const poiRequest = useComponentDataByIri<MapDataV5_jsonld_page_read>(poiRequestTargetWithIds, {
        queryName: 'mapPoiRequestTargetWithIds',
    });

    const poiLivePriceRequest = useComponentDataByIri<MapDataV5_jsonld_page_read>(
        poiRequest.data?.meta.poiLivePricesRequestTarget ?? null,
        { queryName: 'mapPoiLivePricesRequestTarget' },
    );

    const mapDataByPoi = poiLivePriceRequest.data ?? poiRequest.data;

    // hydrate poi state atom
    React.useEffect(() => {
        if (isMapDataV5ByBoundariesLoading) {
            return;
        }
        const timeout = setTimeout(() => {
            setMapDataByPoi(mapDataByPoi);
        }, 300); // skip boundaries loading states
        return () => {
            clearTimeout(timeout);
        };
    }, [isMapDataV5ByBoundariesLoading, mapDataByPoi, setMapDataByPoi]);

    // update loading state
    React.useEffect(() => {
        setPoiLivePricesLoading(poiLivePriceRequest.isLoading);
    }, [poiLivePriceRequest.isLoading, setPoiLivePricesLoading]);
};

type mapUrlParams =
    | 'activityAreaId'
    | 'destination'
    | 'fromLatitude'
    | 'fromLongitude'
    | 'poiId'
    | 'toLatitude'
    | 'toLongitude'
    | 'zoom';

export const useUpdateMapClientUrl = () => {
    const { navigate } = useRouterLink();
    const [clientUrl] = useClientUrl();
    const googleMapInstance = useGoogleMapInstance();

    return ({
        namespace,
        remove = [],
        update = [],
    }: {
        namespace: string;
        remove?: mapUrlParams[];
        update?: mapUrlParams[];
    }) => {
        if (!googleMapInstance) {
            return;
        }

        const boundaries = googleMapInstance.getBounds();
        const zoom = googleMapInstance.getZoom();

        if (!boundaries || !zoom) {
            return;
        }

        const mapBoundaries = getBoundariesObject(boundaries, zoom);

        if (!mapBoundaries) {
            return;
        }

        const newUrl = getURL(clientUrl);
        remove.forEach((param) => {
            if (!clientUrl.includes(param)) {
                return;
            }

            newUrl.searchParams.delete(param);
        });

        update.forEach((param) => {
            switch (param) {
                case 'fromLatitude':
                    newUrl.searchParams.set('fromLatitude', mapBoundaries.fromLatitude);
                    break;
                case 'toLatitude':
                    newUrl.searchParams.set('toLatitude', mapBoundaries.toLatitude);
                    break;
                case 'fromLongitude':
                    newUrl.searchParams.set('fromLongitude', mapBoundaries.fromLongitude);
                    break;
                case 'toLongitude':
                    newUrl.searchParams.set('toLongitude', mapBoundaries.toLongitude);
                    break;
                case 'zoom':
                    newUrl.searchParams.set('zoom', mapBoundaries.zoom);
                    break;
            }
        });

        logMap('Map: update url', namespace);

        navigate(getUrlPathWithSearch(newUrl), {
            disableLinkClickTracking: true,
            disableLoadingAnimation: true,
            disableScrollHistory: true,
            historyType: 'historyReplace',
            type: 'map_update',
        });
    };
};

export const useResetMap = () => {
    const appEvents = useAppEvents();

    return () => appEvents.emit('reset_map');
};
